'use client';

import { useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { workspaceService } from "@/services/workspace";
import type { Workspace } from "@/services/workspace/workspaceService";
import type { AccountTier } from "@/services/workspace/workspaceService";
import { companyService, type DetectResult } from "@/services/company";
import { isValidDomain, normalizeDomain, nameFromDomain } from "../../utils/domain";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Input } from "./input";
import { Label } from "./label";
import { Button } from "./button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

export interface CreateWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  accountName: string;
  isHoldingPen?: boolean;
  userId: string | null;
  sourceWorkspaceId?: string;
  onCreated: (workspace: Workspace) => void;
}

/**
 * Domain-first workspace creation. The domain is REQUIRED and research always
 * starts: the modal creates the workspace, detects the company from it, and hands
 * the user to the setup wizard.
 *
 * There used to be a "Run AI research now" checkbox, and unchecking it was a dead
 * end — it created a workspace with no company row, which meant `/onboarding`
 * 404'd and every data page answered `400 No company configured`. "Set it up
 * later" now means the wizard, which is reachable, rather than a console that
 * isn't.
 */
export function CreateWorkspaceModal({
  open,
  onOpenChange,
  accountId,
  accountName,
  isHoldingPen = false,
  userId,
  sourceWorkspaceId,
  onCreated,
}: CreateWorkspaceModalProps) {
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [testingTier, setTestingTier] = useState<AccountTier>('starter');
  const [resumeWorkspace, setResumeWorkspace] = useState<Workspace | null>(null);
  const [provisioningError, setProvisioningError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setName("");
    setDomain("");
    setTestingTier('starter');
    setResumeWorkspace(null);
    setProvisioningError(null);
    setIsCreating(false);
  }, []);

  const handleOpenChange = useCallback((next: boolean) => {
    // Once the workspace row exists, closing and retrying would create a
    // duplicate. Keep the resumable setup visible until its company bootstrap
    // succeeds; a reload still leaves the one created workspace in the switcher.
    if (isCreating || (resumeWorkspace && provisioningError)) return;
    if (!next) reset();
    onOpenChange(next);
  }, [isCreating, reset, onOpenChange]);

  const trimmedName = name.trim();
  const normalizedDomain = normalizeDomain(domain);
  const domainIsValid = isValidDomain(domain);
  const canSubmit = !!userId && domainIsValid;

  const handleCreate = useCallback(async () => {
    if (!userId || isCreating) return;
    const domainRoot = normalizedDomain;
    if (!domainRoot || !domainIsValid) return;

    setIsCreating(true);
    setProvisioningError(null);
    let ws = resumeWorkspace;
    try {
      const workspaceName = trimmedName || nameFromDomain(domainRoot);
      if (!ws) {
        ws = await workspaceService.createWorkspace({
          workspace_name: workspaceName,
          user_id: userId,
          account_id: accountId,
          source_workspace_id: sourceWorkspaceId,
          ...(isHoldingPen ? { testing_tier: testingTier } : {}),
        });
        setResumeWorkspace(ws);
      }

      // Best-effort detection — never block creation on a slow/failed GPT call.
      let detect: DetectResult | null = null;
      const detectController = new AbortController();
      const detectTimeout = window.setTimeout(() => detectController.abort(), 8_000);
      try {
        detect = await companyService.detectDomain(ws.id, domainRoot, detectController.signal);
      } catch {
        detect = null;
      } finally {
        window.clearTimeout(detectTimeout);
      }
      // Saving settings upserts the company AND dispatches the research job.
      await companyService.saveSettings(ws.id, {
        // Workspace name and company name are different facts. The former is a
        // private switcher label; using it here quietly taught every research
        // prompt that "EMEA launch" (or a QA label) was the brand. Company
        // identity comes only from the domain detection, with the domain itself
        // as the deterministic fallback.
        name: detect?.company_name || nameFromDomain(domainRoot),
        domain: domainRoot,
        category: detect?.category || undefined,
      });

      // Hand off FIRST and leave the dialog up. `onCreated` switches workspace and
      // navigates, and the ~500ms in between is when every query re-keys against
      // the OLD route — closing here is what made that gap visible as a flash of
      // the previous workspace's page. `handleOpenChange` already refuses to close
      // mid-create, so "Creating…" stays until the browser leaves.
      onCreated(ws);
    } catch (error) {
      console.error("Failed to create workspace:", error);
      if (ws) {
        const message = "The workspace was created, but setup could not finish. Retry resumes this workspace without creating a duplicate.";
        setProvisioningError(message);
        toast.error(message);
      } else {
        toast.error("Failed to create workspace. Please try again.");
      }
      setIsCreating(false);
    }
  }, [userId, isCreating, normalizedDomain, domainIsValid, trimmedName, accountId, sourceWorkspaceId, isHoldingPen, testingTier, resumeWorkspace, onCreated]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg">New workspace</DialogTitle>
          <DialogDescription>in {accountName}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cw-domain">Company domain</Label>
            <Input
              id="cw-domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="acme.com"
              autoFocus
              disabled={isCreating || !!resumeWorkspace}
              onKeyDown={(e) => { if (e.key === "Enter" && canSubmit) handleCreate(); }}
            />
            <p className="text-xs text-ink-supporting">
              We'll detect your company, then walk you through setup.
            </p>
            {domain.length > 0 && !domainIsValid && (
              <p className="text-xs text-destructive" role="alert">
                Enter a valid company domain, such as merge.dev.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cw-name">
              Workspace name <span className="font-normal text-ink-supporting">(optional)</span>
            </Label>
            <Input
              id="cw-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Defaults to your company name"
              disabled={isCreating || !!resumeWorkspace}
              onKeyDown={(e) => { if (e.key === "Enter" && canSubmit) handleCreate(); }}
            />
          </div>

          {isHoldingPen && (
            <div className="space-y-1.5">
              <Label htmlFor="cw-testing-tier">Plan to test</Label>
              <Select value={testingTier} onValueChange={(value) => setTestingTier(value as AccountTier)} disabled={isCreating || !!resumeWorkspace}>
                <SelectTrigger id="cw-testing-tier">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-ink-supporting">
                This holding-pen workspace will enforce the selected plan's product, prompt, engine, and head-to-head limits.
              </p>
            </div>
          )}

          {provisioningError && (
            <p className="text-xs text-destructive" role="alert">
              {provisioningError}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isCreating || !!provisioningError}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!canSubmit || isCreating}>
            {isCreating && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {isCreating ? "Creating…" : resumeWorkspace ? "Retry setup" : "Create & set up"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
